(() => {
  // utils/components/cart-performance.ts
  class CartPerformance {
    static #metric_prefix = "cart-performance";
    static createStartingMarker(benchmarkName) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      return performance.mark(`${metricName}:start`);
    }
    static measureFromEvent(benchmarkName, event) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const startMarker = performance.mark(`${metricName}:start`, {
        startTime: event.timeStamp,
      });
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(
        metricName,
        `${metricName}:start`,
        `${metricName}:end`,
      );
    }
    static measureFromMarker(benchmarkName, startMarker) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(metricName, startMarker.name, `${metricName}:end`);
    }
    static measure(benchmarkName, callback) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const startMarker = performance.mark(`${metricName}:start`);
      callback();
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(
        metricName,
        `${metricName}:start`,
        `${metricName}:end`,
      );
    }
  }
  // utils/getFocusableElements.ts
  var getFocusableElements = (container) => {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
      ),
    );
  };

  // utils/trapFocus.ts
  var trapFocusHandlers = {};
  var trapFocus = (container, elementToFocus = container) => {
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];
    removeTrapFocus();
    trapFocusHandlers.focusin = (event) => {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;
      if (trapFocusHandlers.keydown) {
        document.addEventListener("keydown", trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.focusout = () => {
      if (trapFocusHandlers.keydown) {
        document.removeEventListener("keydown", trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.keydown = (event) => {
      if (event.code.toUpperCase() !== "TAB") return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("focusout", trapFocusHandlers.focusout);
    document.addEventListener("focusin", trapFocusHandlers.focusin);
    elementToFocus.focus();
    if (elementToFocus instanceof HTMLInputElement) {
      if (
        ["search", "text", "email", "url"].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    }
  };
  var removeTrapFocus = (elementToFocus = null) => {
    if (trapFocusHandlers.focusin) {
      document.removeEventListener("focusin", trapFocusHandlers.focusin);
    }
    if (trapFocusHandlers.focusout) {
      document.removeEventListener("focusout", trapFocusHandlers.focusout);
    }
    if (trapFocusHandlers.keydown) {
      document.removeEventListener("keydown", trapFocusHandlers.keydown);
    }
    if (elementToFocus) elementToFocus.focus();
  };
  // utils/components/modal-dialog.ts
  class ModalDialog extends HTMLElement {
    openedBy;
    moved;
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]')?.addEventListener(
        "click",
        this.hide.bind(this),
      );
      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });
      if (this.classList.contains("media-modal")) {
        this.addEventListener("pointerup", (event) => {
          if (
            event.pointerType === "mouse" &&
            !event.target.closest("deferred-media, product-model")
          )
            this.hide();
        });
      } else {
        this.addEventListener("click", (event) => {
          if (event.target === this) this.hide();
        });
      }
    }
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      const section = this.closest(".shopify-section");
      if (section) {
        this.dataset.section = section.id.replace("shopify-section-", "");
      }
      document.body.appendChild(this);
    }
    show(opener) {
      this.openedBy = opener;
      const popup = this.querySelector(".template-popup");
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      if (popup) popup.loadContent();
      const dialog = this.querySelector('[role="dialog"]');
      if (dialog) {
        trapFocus(this, dialog);
      }
      window.pauseAllMedia();
    }
    hide() {
      document.body.classList.remove("overflow-hidden");
      document.body.dispatchEvent(new CustomEvent("modalClosed"));
      this.removeAttribute("open");
      removeTrapFocus(this.openedBy ?? null);
      window.pauseAllMedia();
    }
  }
  // assets/scripts/product/product-modal.ts
  class ProductModal extends ModalDialog {
    constructor() {
      super();
    }
    hide() {
      super.hide();
    }
    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }
    showActiveMedia() {
      const mediaId = this.openedBy?.getAttribute("data-media-id");
      if (!mediaId) return;
      this.querySelectorAll(
        `[data-media-id]:not([data-media-id="${mediaId}"])`,
      ).forEach((element) => {
        element.classList.remove("active");
      });
      const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;
      const activeMediaTemplate = activeMedia.querySelector("template");
      const activeMediaContent = activeMediaTemplate
        ? activeMediaTemplate.content
        : null;
      activeMedia.classList.add("active");
      activeMedia.scrollIntoView();
      const container = this.querySelector('[role="document"]');
      if (container) {
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
      }
      if (
        activeMedia.nodeName === "DEFERRED-MEDIA" &&
        activeMediaContent &&
        activeMediaContent.querySelector(".js-youtube")
      ) {
        activeMedia.loadContent();
      }
    }
  }
  if (!customElements.get("product-modal")) {
    customElements.define("product-modal", ProductModal);
  }
})();
